/**
 * eReg Network Documents (Phase 5 — plan §5.7)
 *
 * Network-zone reference documents shared across every site in the current
 * site's network — SOPs, guidance, cross-site material. Visible to every
 * eReg user; folder creation and document writes are administrator-only
 * (the server enforces this with 403 EREG_NETWORK_ADMIN_REQUIRED).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Globe,
  Info,
  Folder as FolderIcon,
  FolderOpen,
  FileText,
  Plus,
  Upload as UploadIcon,
  MoreVertical,
  Eye,
  Download,
  Pencil,
  FilePlus2,
  Archive,
  ArchiveRestore,
  RotateCcw,
  RefreshCw,
  Trash2,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEreg } from '../contexts/EregContext';
import * as eregApi from '../lib/api';
import {
  PageHeader,
  RefreshButton,
  EmptyState,
  Modal,
  ConfirmModal,
  fieldCls,
  primaryBtnCls,
  secondaryBtnCls,
  useToast,
} from '../../ui';
import { openPresigned } from '../../ui/studyRoles';
import type { Folder, Document, DocumentType } from '../types';

/** Flatten a folder tree (or an already-flat list) into path order. */
function flattenFolders(tree: Folder[]): Folder[] {
  const out: Folder[] = [];
  const seen = new Set<string>();
  const walk = (nodes: Folder[]) => {
    for (const node of nodes || []) {
      if (!seen.has(node.id)) {
        seen.add(node.id);
        out.push(node);
      }
      if (node.children && node.children.length > 0) walk(node.children);
    }
  };
  walk(tree || []);
  return out.sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name));
}

function folderDepth(folder: Folder): number {
  const parts = (folder.path || folder.name).split('/').filter(Boolean);
  return Math.max(parts.length - 1, 0);
}

function isNetworkAdminError(err: any): boolean {
  return err?.response?.data?.code === 'EREG_NETWORK_ADMIN_REQUIRED';
}

export function NetworkDocuments() {
  const { currentSite } = useAuth();
  const { isAdmin, documentTypes } = useEreg();
  const { toast } = useToast();

  const networkId = currentSite?.network_id;
  const networkName = currentSite?.networks?.name || 'your network';

  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const selectedFolder = folders.find((f) => f.id === selectedFolderId) || null;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Modals / actions
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [renameDoc, setRenameDoc] = useState<Document | null>(null);
  const [versionDoc, setVersionDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [deleteText, setDeleteText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [menuDocId, setMenuDocId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const loadFolders = useCallback(async () => {
    if (!networkId) return;
    setFoldersLoading(true);
    try {
      const tree = await eregApi.getNetworkFolders(networkId);
      const flat = flattenFolders(tree);
      setFolders(flat);
      setSelectedFolderId((prev) =>
        prev && flat.some((f) => f.id === prev) ? prev : flat[0]?.id || null
      );
    } catch (err) {
      console.error('[eReg] Failed to load network folders:', err);
      toast('Failed to load network folders', { type: 'error' });
    } finally {
      setFoldersLoading(false);
    }
  }, [networkId, toast]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const loadDocuments = useCallback(async () => {
    if (!selectedFolderId) {
      setDocuments([]);
      return;
    }
    setDocumentsLoading(true);
    try {
      const docs = await eregApi.getFolderDocuments(selectedFolderId, {
        include_archived: showArchived,
      });
      setDocuments(docs);
    } catch (err) {
      console.error('[eReg] Failed to load network documents:', err);
      toast('Failed to load documents', { type: 'error' });
    } finally {
      setDocumentsLoading(false);
    }
  }, [selectedFolderId, showArchived, toast]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Close the row menu on outside click
  useEffect(() => {
    if (!menuDocId) return;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuDocId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuDocId]);

  const openDoc = async (doc: Document, download = false) => {
    const opened = await openPresigned(async () => {
      const { url } = download
        ? await eregApi.getDocumentDownloadUrl(doc.id)
        : await eregApi.getDocumentViewUrl(doc.id);
      return url;
    });
    if (!opened) toast('Failed to open document', { type: 'error' });
  };

  const handleArchiveToggle = async (doc: Document) => {
    try {
      await eregApi.updateDocument(doc.id, {
        status: doc.status === 'archived' ? 'active' : 'archived',
      });
      toast(doc.status === 'archived' ? 'Document unarchived' : 'Document archived');
      await loadDocuments();
    } catch (err: any) {
      console.error('[eReg] Failed to update document:', err);
      toast(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network documents'
          : err?.response?.data?.code === 'EREG_KEY_DOCUMENT_IN_USE'
            ? err.response.data.error
            : 'Failed to update document',
        { type: 'error' }
      );
    }
  };

  const handleRestore = async (doc: Document) => {
    try {
      await eregApi.restoreDocument(doc.id);
      toast('Document restored');
      await loadDocuments();
    } catch (err) {
      console.error('[eReg] Failed to restore document:', err);
      toast('Failed to restore document', { type: 'error' });
    }
  };

  const handleReextract = async (doc: Document) => {
    try {
      await eregApi.reextractDocumentText(doc.id);
      toast('Text extraction queued — the current version will re-extract shortly');
    } catch (err) {
      console.error('[eReg] Failed to queue re-extraction:', err);
      toast('Failed to queue text extraction', { type: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    if (deleteText.trim() !== deleteDoc.document_name) {
      setDeleteError('The typed name does not match the document name');
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await eregApi.deleteDocument(deleteDoc.id);
      toast('Document deleted');
      setDeleteDoc(null);
      setDeleteText('');
      await loadDocuments();
    } catch (err: any) {
      console.error('[eReg] Failed to delete document:', err);
      setDeleteError(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network documents'
          : err?.response?.data?.code === 'EREG_KEY_DOCUMENT_IN_USE'
            ? err.response.data.error
            : 'Failed to delete document'
      );
    } finally {
      setDeleting(false);
    }
  };

  if (!currentSite || !networkId) {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <Globe size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h2 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">No Site Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select a site from the header to view its network documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title="Network Documents"
        icon={Globe}
        subtitle={`Reference documents shared across ${networkName} — SOPs, guidance, cross-site material`}
        actions={
          <>
            <RefreshButton
              onClick={() => {
                loadFolders();
                loadDocuments();
              }}
              loading={foldersLoading || documentsLoading}
            />
            {isAdmin && (
              <>
                <button type="button" onClick={() => setShowNewFolder(true)} className={secondaryBtnCls}>
                  <Plus size={16} />
                  New Folder
                </button>
                <button
                  type="button"
                  onClick={() => setShowUpload(true)}
                  disabled={folders.length === 0}
                  className={primaryBtnCls}
                >
                  <UploadIcon size={16} />
                  Upload
                </button>
              </>
            )}
          </>
        }
      />

      {/* Visibility hint */}
      <div className="flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
        <Info size={16} className="mt-0.5 flex-shrink-0" />
        <span>Documents here are visible to every site in {networkName}.</span>
      </div>

      {foldersLoading && folders.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : folders.length === 0 ? (
        <EmptyState
          icon={Globe}
          variant="card"
          title="No network folders yet"
          hint={`Folders created here hold reference documents shared by every site in ${networkName}.`}
          action={
            isAdmin ? (
              <button type="button" onClick={() => setShowNewFolder(true)} className={primaryBtnCls}>
                <Plus size={16} />
                Create your first folder
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Folder rail */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Folders</h3>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {folders.map((folder) => {
                const depth = folderDepth(folder);
                const active = folder.id === selectedFolderId;
                return (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => setSelectedFolderId(folder.id)}
                    className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
                      active
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                    style={{ paddingLeft: `${16 + depth * 16}px` }}
                  >
                    {active ? (
                      <FolderOpen size={18} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    ) : (
                      <FolderIcon size={18} className="flex-shrink-0 text-gray-400 dark:text-gray-500" />
                    )}
                    <span
                      className={`min-w-0 flex-1 truncate text-sm ${
                        active
                          ? 'font-medium text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                      title={folder.path || folder.name}
                    >
                      {folder.name}
                    </span>
                    {typeof folder.document_count === 'number' && folder.document_count > 0 && (
                      <span className="flex-shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">
                        {folder.document_count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Documents */}
          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
            <div className="flex items-center justify-between gap-2 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
              <h3 className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">
                Documents {selectedFolder && `in ${selectedFolder.name}`}
              </h3>
              <label className="flex cursor-pointer select-none items-center gap-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                Show archived &amp; deleted
              </label>
            </div>

            {documentsLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
              </div>
            ) : documents.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No documents in this folder"
                hint={isAdmin ? 'Use Upload to add the first document.' : 'Nothing has been shared here yet.'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Version</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Updated</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {documents.map((doc) => {
                      const isDeleted = Boolean(doc.deleted_at);
                      const isArchived = doc.status === 'archived';
                      return (
                        <tr
                          key={doc.id}
                          className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isDeleted ? 'opacity-60' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => !isDeleted && openDoc(doc)}
                                className="max-w-[16rem] truncate text-left text-sm font-medium text-gray-900 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
                                title={doc.document_name}
                              >
                                {doc.document_name}
                              </button>
                              {isDeleted ? (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                  Deleted
                                </span>
                              ) : isArchived ? (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/60 dark:text-gray-300">
                                  Archived
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{doc.document_type}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">v{doc.version_number}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                            {new Date(doc.updated_at).toLocaleDateString()}
                          </td>
                          <td className="relative px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => setMenuDocId(menuDocId === doc.id ? null : doc.id)}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                              aria-label={`Actions for ${doc.document_name}`}
                            >
                              <MoreVertical size={16} />
                            </button>
                            {menuDocId === doc.id && (
                              <div
                                ref={menuRef}
                                className="absolute right-4 top-11 z-20 w-52 rounded-xl border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800"
                              >
                                {isDeleted ? (
                                  isAdmin ? (
                                    <MenuItem icon={<RotateCcw size={14} />} label="Restore" onClick={() => { setMenuDocId(null); handleRestore(doc); }} />
                                  ) : (
                                    <p className="px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500">No actions available</p>
                                  )
                                ) : (
                                  <>
                                    <MenuItem icon={<Eye size={14} />} label="View" onClick={() => { setMenuDocId(null); openDoc(doc); }} />
                                    <MenuItem icon={<Download size={14} />} label="Download" onClick={() => { setMenuDocId(null); openDoc(doc, true); }} />
                                    {isAdmin && (
                                      <>
                                        <MenuItem icon={<FilePlus2 size={14} />} label="Upload new version" onClick={() => { setMenuDocId(null); setVersionDoc(doc); }} />
                                        <MenuItem icon={<Pencil size={14} />} label="Rename" onClick={() => { setMenuDocId(null); setRenameDoc(doc); }} />
                                        <MenuItem icon={<RefreshCw size={14} />} label="Re-extract text" onClick={() => { setMenuDocId(null); handleReextract(doc); }} />
                                        <MenuItem
                                          icon={isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                                          label={isArchived ? 'Unarchive' : 'Archive'}
                                          onClick={() => { setMenuDocId(null); handleArchiveToggle(doc); }}
                                        />
                                        <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                                        <MenuItem
                                          icon={<Trash2 size={14} />}
                                          label="Delete"
                                          danger
                                          onClick={() => { setMenuDocId(null); setDeleteDoc(doc); setDeleteText(''); setDeleteError(null); }}
                                        />
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New folder (admin) */}
      {showNewFolder && networkId && (
        <NewNetworkFolderModal
          networkId={networkId}
          folders={folders}
          defaultParentId={selectedFolderId}
          onClose={() => setShowNewFolder(false)}
          onCreated={() => {
            setShowNewFolder(false);
            toast('Folder created');
            loadFolders();
          }}
        />
      )}

      {/* Upload (admin) */}
      {showUpload && (
        <NetworkUploadModal
          folders={folders}
          defaultFolderId={selectedFolderId}
          documentTypes={documentTypes}
          onClose={() => setShowUpload(false)}
          onUploaded={(folderId) => {
            setShowUpload(false);
            toast('Document uploaded');
            if (folderId === selectedFolderId) loadDocuments();
            else setSelectedFolderId(folderId);
          }}
        />
      )}

      {/* Rename (admin) */}
      {renameDoc && (
        <RenameNetworkDocumentModal
          document={renameDoc}
          onClose={() => setRenameDoc(null)}
          onRenamed={() => {
            setRenameDoc(null);
            toast('Document renamed');
            loadDocuments();
          }}
        />
      )}

      {/* New version (admin) */}
      {versionDoc && (
        <NewNetworkVersionModal
          document={versionDoc}
          onClose={() => setVersionDoc(null)}
          onUploaded={() => {
            setVersionDoc(null);
            toast('New version uploaded');
            loadDocuments();
          }}
        />
      )}

      {/* Delete (admin, type-to-confirm) */}
      <ConfirmModal
        isOpen={!!deleteDoc}
        onClose={() => { setDeleteDoc(null); setDeleteText(''); setDeleteError(null); }}
        onConfirm={handleDelete}
        title="Delete Document"
        danger
        loading={deleting}
        confirmLabel="Delete"
        message={
          deleteDoc ? (
            <div className="space-y-3">
              <p>
                This deletes{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">{deleteDoc.document_name}</span>{' '}
                for every site in the network. It moves to the deleted list and can be restored later;
                the action is recorded in the audit trail.
              </p>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Type the document name to confirm
                </label>
                <input
                  type="text"
                  value={deleteText}
                  onChange={(e) => { setDeleteText(e.target.value); setDeleteError(null); }}
                  placeholder={deleteDoc.document_name}
                  className={`w-full ${fieldCls}`}
                  autoFocus
                />
              </div>
              {deleteError && <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>}
            </div>
          ) : undefined
        }
      />
    </div>
  );
}

// Row menu item (same recipe as Folders.tsx)
function MenuItem({ icon, label, onClick, danger = false }: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors duration-150 ${
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// =====================================================
// New network folder modal (admin)
// =====================================================

function NewNetworkFolderModal({
  networkId,
  folders,
  defaultParentId,
  onClose,
  onCreated,
}: {
  networkId: string;
  folders: Folder[];
  defaultParentId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState(defaultParentId || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await eregApi.createFolder({
        network_id: networkId,
        name: name.trim(),
        parent_folder_id: parentId || undefined,
      });
      onCreated();
    } catch (err: any) {
      console.error('[eReg] Failed to create network folder:', err);
      setError(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network folders'
          : err?.response?.data?.error || 'Failed to create folder'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New Network Folder" maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Folder Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full ${fieldCls}`}
            placeholder="e.g., Network SOPs"
            autoFocus
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Parent Folder</label>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} className={`w-full ${fieldCls}`}>
            <option value="">Top level</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.path || folder.name}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting || !name.trim()} className={primaryBtnCls}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus size={14} />}
            {submitting ? 'Creating…' : 'Create Folder'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =====================================================
// Upload into a network folder (admin) — reuses the AI metadata suggestion
// =====================================================

function NetworkUploadModal({
  folders,
  defaultFolderId,
  documentTypes,
  onClose,
  onUploaded,
}: {
  folders: Folder[];
  defaultFolderId: string | null;
  documentTypes: DocumentType[];
  onClose: () => void;
  onUploaded: (folderId: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [folderId, setFolderId] = useState(defaultFolderId || folders[0]?.id || '');
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [versionNumber, setVersionNumber] = useState('1.0');
  const [suggesting, setSuggesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setDocumentName(selectedFile.name.replace(/\.[^.]+$/, ''));
    if (selectedFile.type === 'application/pdf') {
      setSuggesting(true);
      try {
        const suggestions = await eregApi.suggestMetadata(selectedFile);
        setDocumentName((prev) => suggestions.document_name || prev);
        setDocumentType((prev) => suggestions.document_type || prev);
      } catch (err) {
        console.error('[eReg] Metadata suggestion failed:', err);
      } finally {
        setSuggesting(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !folderId || !documentName.trim() || !documentType) {
      setError('Choose a file, folder, name, and document type');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await eregApi.uploadDocument(file, {
        folder_id: folderId,
        document_name: documentName.trim(),
        document_type: documentType,
        version_number: versionNumber.trim() || undefined,
      });
      onUploaded(folderId);
    } catch (err: any) {
      console.error('[eReg] Failed to upload network document:', err);
      setError(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network documents'
          : err?.response?.data?.error || 'Failed to upload document'
      );
    } finally {
      setUploading(false);
    }
  };

  const typeInList = documentTypes.some((t) => t.name === documentType);

  return (
    <Modal isOpen onClose={onClose} title="Upload Network Document" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">File *</label>
          <button type="button" onClick={() => fileInputRef.current?.click()} className={secondaryBtnCls}>
            <FilePlus2 size={14} />
            {file ? file.name : 'Choose file'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.csv,.txt"
          />
          {suggesting && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400">
              <Sparkles size={12} className="animate-pulse" />
              Analyzing document for metadata suggestions…
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Folder *</label>
          <select value={folderId} onChange={(e) => setFolderId(e.target.value)} className={`w-full ${fieldCls}`} required>
            <option value="">Select folder…</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.path || folder.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Name *</label>
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            className={`w-full ${fieldCls}`}
            required
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Type *</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select type…</option>
              {!typeInList && documentType && <option value={documentType}>{documentType}</option>}
              {documentTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
            <input
              type="text"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              placeholder="1.0"
              className={`w-full ${fieldCls}`}
            />
          </div>
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading || !file || !folderId || !documentName.trim() || !documentType}
            className={primaryBtnCls}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon size={14} />}
            {uploading ? 'Uploading…' : 'Upload Document'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =====================================================
// Rename modal (admin)
// =====================================================

function RenameNetworkDocumentModal({
  document,
  onClose,
  onRenamed,
}: {
  document: Document;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(document.document_name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === document.document_name) return;
    setSaving(true);
    setError(null);
    try {
      await eregApi.updateDocument(document.id, { document_name: name.trim() });
      onRenamed();
    } catch (err: any) {
      console.error('[eReg] Failed to rename document:', err);
      setError(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network documents'
          : 'Failed to rename document'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Rename Document" maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full ${fieldCls}`}
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim() || name.trim() === document.document_name}
            className={primaryBtnCls}
          >
            {saving ? 'Renaming…' : 'Rename'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =====================================================
// New version modal (admin)
// =====================================================

function NewNetworkVersionModal({
  document,
  onClose,
  onUploaded,
}: {
  document: Document;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [versionNumber, setVersionNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !versionNumber.trim()) {
      setError('Choose a file and enter a version label');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await eregApi.uploadDocumentVersion(document.id, file, versionNumber.trim());
      onUploaded();
    } catch (err: any) {
      console.error('[eReg] Failed to upload version:', err);
      setError(
        isNetworkAdminError(err)
          ? 'Administrator access is required to manage network documents'
          : 'Failed to upload new version'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Upload New Version" maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
          <p className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</p>
          <p className="text-gray-500 dark:text-gray-400">Current version: v{document.version_number}</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">File *</label>
          <button type="button" onClick={() => fileInputRef.current?.click()} className={secondaryBtnCls}>
            <FilePlus2 size={14} />
            {file ? file.name : 'Choose file'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.csv,.txt"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">New Version Label *</label>
          <input
            type="text"
            value={versionNumber}
            onChange={(e) => setVersionNumber(e.target.value)}
            placeholder="e.g., 2.0"
            className={`w-full ${fieldCls}`}
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button type="submit" disabled={uploading || !file || !versionNumber.trim()} className={primaryBtnCls}>
            {uploading ? 'Uploading…' : 'Upload Version'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default NetworkDocuments;
